import * as React from 'react'
import produce from 'immer'
import { useImmer } from 'use-immer'
import { Input, List, Spin, Radio } from 'antd'
import { isEmpty, toLower, set } from 'lodash'
import { showError } from 'helpers/errors'
import { t } from 'helpers/i18n'
import { stopEvent } from 'helpers/events'
import Help from 'elements/Help'
import { Row, Col } from 'elements/Grid'

function Component(props) {
  const { onChange, values, readOnly, getSettings, disabled } = props

  const [state, updateState] = useImmer({})
  const { fieldSettings, filterBy = '' } = state

  const setState = React.useCallback((name, value) => {
    updateState((draft) => {
      set(draft, name, value)
    })
  }, [])

  async function fetchSettings() {
    try {
      const response = await getSettings({ type: 'permissions' })

      setState('fieldSettings', response.value.data.fieldSettings)
    } catch (error) {
      showError({ error })
    }
  }

  React.useEffect(() => {
    fetchSettings()
  }, [])

  if (!fieldSettings) {
    return <Spin />
  }

  function getPermission(dtoFieldName) {
    return values.find((one) => toLower(one.permissionType) === dtoFieldName) || {}
  }

  function isDisabled(dtoFieldName) {
    if (disabled) {
      return true
    }

    if (dtoFieldName === 'master') {
      return false
    }

    if (getPermission('master').value === 'Yes') {
      return ![
        'ReceiveOrderApprovalRequests',
        'ReceiveJobCompletionNotifications',
        'ReceiveJobApprovalRequests',
        'PurchasingLimit',
      ]
        .map(toLower)
        .includes(dtoFieldName)
    }

    return false
  }

  function setValue(dtoFieldName, value) {
    if (!readOnly) {
      onChange(
        produce(values, (draft) => {
          const permission = draft.find((one) => toLower(one.permissionType) === dtoFieldName)

          if (permission) {
            permission.value = value
          } else {
            draft.push({ permissionType: dtoFieldName, value })
          }
        })
      )
    }
  }

  function setModifier(dtoFieldName, modifier) {
    if (!readOnly) {
      onChange(
        produce(values, (draft) => {
          const permission = draft.find((one) => toLower(one.permissionType) === dtoFieldName)

          if (permission) {
            permission.modifier = modifier
          } else {
            draft.push({ permissionType: dtoFieldName, modifier })
          }
        })
      )
    }
  }

  const dataSource = fieldSettings.filter((each) => {
    if (isEmpty(filterBy)) {
      return true
    }

    const text = t(each.recordLabelLanguageKey)

    return toLower(text).indexOf(toLower(filterBy)) > -1
  })

  return (
    <>
      <Row className="mb-18">
        <Col third>
          <Input.Search
            placeholder={t('search')}
            defaultValue={filterBy}
            onChange={(e) => setState('filterBy', e.target.value)}
            disabled={disabled}
            maxLength={200}
            allowClear
          />
        </Col>
      </Row>
      <List
        header={
          <Row className="w-full">
            <Col xs={12} sm={8}>
              {t('permission')}
            </Col>
            <Col xs={12} sm={16}>
              {t('value')}
            </Col>
          </Row>
        }
        dataSource={dataSource}
        renderItem={({ recordLabelLanguageKey, recordInfoLanguageKey, ...rest }) => {
          const dtoFieldName = toLower(rest.dtoFieldName)

          return (
            <List.Item>
              <Row className="w-full">
                <Col xs={12} sm={8}>
                  <span dangerouslySetInnerHTML={{ __html: t(recordLabelLanguageKey) }} />{' '}
                  {!isEmpty(recordInfoLanguageKey) && <Help title={t(recordInfoLanguageKey)} />}
                </Col>
                <Col xs={12} sm={16}>
                  {dtoFieldName === 'documentlibrary' ? (
                    <Radio.Group
                      value={getPermission(dtoFieldName).value || 'None'}
                      onChange={(e) => setValue(dtoFieldName, e.target.value)}
                      disabled={isDisabled(dtoFieldName)}
                    >
                      <Radio value="None">{t('none')}</Radio>
                      <Radio value="ReadOnly">{t('readOnly')}</Radio>
                      <Radio value="ReadWrite">{t('readWrite')}</Radio>
                    </Radio.Group>
                  ) : dtoFieldName === 'purchasinglimit' ? (
                    <Row type="flex">
                      <Col>
                        <Radio.Group
                          value={getPermission(dtoFieldName).value || 'None'}
                          onChange={(e) => setValue(dtoFieldName, e.target.value)}
                          disabled={isDisabled(dtoFieldName)}
                        >
                          <Radio value="None">{t('none')}</Radio>
                          <Radio value="Unlimited">{t('unlimited')}</Radio>
                          <Radio value="Value">{t('value')}</Radio>
                        </Radio.Group>
                      </Col>
                      <Col className="vertical-align">
                        <Input
                          className="w-full"
                          value={getPermission(dtoFieldName).modifier}
                          onChange={(e) => setModifier(dtoFieldName, e.target.value)}
                          disabled={isDisabled(dtoFieldName)}
                          style={{
                            visibility: getPermission(dtoFieldName).value === 'Value' ? 'visible' : 'hidden',
                          }}
                          onKeyPress={(e) => {
                            if (e.key.length === 1 && /\D/.test(e.key)) {
                              stopEvent(e)
                            }
                          }}
                        />
                      </Col>
                    </Row>
                  ) : ['orders', 'requisitions', 'rfqs', 'jobs', 'forms'].includes(dtoFieldName) ? (
                    <Radio.Group
                      value={getPermission(dtoFieldName).value || 'None'}
                      onChange={(e) => setValue(dtoFieldName, e.target.value)}
                      disabled={isDisabled(dtoFieldName)}
                    >
                      <Radio value="None">{t('none')}</Radio>
                      <Radio value="Own">{t('own')}</Radio>
                      <Radio value="All">{t('all')}</Radio>
                    </Radio.Group>
                  ) : ['suppliers', 'products', 'inventory', 'assets', 'operators'].includes(dtoFieldName) ? (
                    <Radio.Group
                      value={getPermission(dtoFieldName).value || 'None'}
                      onChange={(e) => setValue(dtoFieldName, e.target.value)}
                      disabled={isDisabled(dtoFieldName)}
                    >
                      <Radio value="None">{t('none')}</Radio>
                      <Radio value="View">{t('view')}</Radio>
                      <Radio value="Edit">{t('edit')}</Radio>
                    </Radio.Group>
                  ) : (
                    <Radio.Group
                      value={getPermission(dtoFieldName).value || 'No'}
                      onChange={(e) => setValue(dtoFieldName, e.target.value)}
                      disabled={isDisabled(dtoFieldName)}
                    >
                      <Radio value="No">{t('no')}</Radio>
                      <Radio value="Yes">{t('yes')}</Radio>
                    </Radio.Group>
                  )}
                </Col>
              </Row>
            </List.Item>
          )
        }}
        bordered
      />
    </>
  )
}

export default Component
